<script lang="ts">
    import { createEventDispatcher, onDestroy } from 'svelte';

    export let name: string;
    export let errors: Record<string, string[]>;
    export let label: string = '';
    export let description: string = '';
    export let checked: boolean = false;
    export let disabled: boolean = false;

    errors[name] = [];

    onDestroy(() => { errors[name] = []; });

    const dispatch = createEventDispatcher();

    const handleChange = () => {
        if (disabled) return;
        checked = !checked;
        dispatch('change', { name, checked });
    };
</script>

<div class="form-field toggle-field">
    <div class="toggle-info">
        {#if label}<span class="label-text">{label}</span>{/if}
        {#if description}<span class="toggle-description">{description}</span>{/if}
    </div>
    <button
        type="button"
        class="toggle-switch"
        class:on={checked}
        {disabled}
        role="switch"
        aria-checked={checked}
        aria-label={label || name}
        on:click={handleChange}
    >
        <span class="track"></span>
        <span class="thumb">
            {#if checked}
                <svg class="check-icon" viewBox="0 0 24 24" width="12" height="12">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                </svg>
            {/if}
        </span>
    </button>
</div>

<style lang="scss">
    .toggle-field {
        display: flex;
        flex-direction: row !important;
        align-items: center;
        justify-content: space-between;
        gap: 1.5em;
        margin-bottom: 1em;
    }

    .toggle-info {
        display: flex;
        flex-direction: column;
        gap: 0.1em;
        flex: 1;

        .label-text {
            position: static !important;
            font-size: 0.95em !important;
            color: $color-text !important;
            text-transform: none !important;
            letter-spacing: normal !important;
            font-weight: 500 !important;
        }
    }

    .toggle-description {
        font-size: 0.82em;
        color: $color-text-secondary;
    }

    // ── M3 Switch Style ───────────────────────────────────────────────────────

    $track-width:  52px;
    $track-height: 32px;
    $thumb-size-off: 16px;
    $thumb-size-on:  24px;

    .toggle-switch {
        all: unset;
        position: relative;
        width: $track-width;
        height: $track-height;
        cursor: pointer;
        display: flex;
        align-items: center;
        outline: none;
        user-select: none;

        &:disabled {
            opacity: 0.38;
            cursor: default;
        }

        &:focus-visible .track {
            outline: 2px solid $color-primary;
            outline-offset: 2px;
        }
    }

    .track {
        position: absolute;
        inset: 0;
        border-radius: 100px;
        background-color: $color-bg-active;
        border: 2px solid $color-text-muted;
        transition: all 0.2s ease;

        .on & {
            background-color: $color-primary;
            border-color: $color-primary;
        }
    }

    .thumb {
        position: absolute;
        left: 8px;
        width: $thumb-size-off;
        height: $thumb-size-off;
        background-color: $color-text-muted;
        border-radius: 50%;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;

        .on & {
            left: calc(100% - #{$thumb-size-on} - 4px);
            width: $thumb-size-on;
            height: $thumb-size-on;
            background-color: white;
            color: $color-primary;
        }

        .toggle-switch:hover:not(:disabled) & {
            background-color: $color-text;
        }

        .on.toggle-switch:hover:not(:disabled) & {
            background-color: #f0f0f0;
        }
    }

    .check-icon {
        opacity: 0;
        transform: scale(0.5);
        transition: all 0.2s ease;

        .on & {
            opacity: 1;
            transform: scale(1);
        }
    }
</style>
